import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";

export const BLOCKER_YELLOW = "#ffe606";

export function BlockerBadge({ note, testId, iconOnly }) {
  const showNote = !iconOnly && note;
  return (
    <span data-testid={testId} title={note || "Blocker"}
      className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full flex-shrink-0 ${showNote ? "px-2 py-0.5 max-w-[260px]" : "px-1.5 py-0.5"}`}
      style={{ backgroundColor: BLOCKER_YELLOW, color: "#0A1F44" }}>
      <AlertTriangle size={12} className="flex-shrink-0" />
      {showNote && <span className="truncate">{note}</span>}
    </span>
  );
}

function BlockerNoteModal({ text, setText, onConfirm, onCancel, testPrefix }) {
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,31,68,0.6)" }}
      onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 border" style={{ borderColor: "#E5E7EB" }} data-testid={`${testPrefix}-modal`}>
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <AlertTriangle size={18} style={{ color: "#d4be00" }} /> Flag as holding up the build
        </h3>
        <p className="text-sm text-[#6B7280] mt-1 mb-3">
          The client will see a warning marker on this item in their Review portal until you remove it.
        </p>
        <textarea data-testid={`${testPrefix}-note-input`} value={text} onChange={(e) => setText(e.target.value)} rows={2} autoFocus
          placeholder='Optional note shown to the client, e.g. "Waiting on your signature"'
          className="w-full rounded-lg px-3 py-2 text-sm outline-none border focus:ring-2 focus:ring-[#F59E0B] resize-y" style={{ borderColor: "#E5E7EB" }} />
        <div className="flex gap-2 mt-4">
          <button data-testid={`${testPrefix}-cancel`} onClick={onCancel}
            className="flex-1 rounded-lg px-4 py-2 text-sm font-medium border" style={{ borderColor: "#E5E7EB" }}>Cancel</button>
          <button data-testid={`${testPrefix}-confirm`} onClick={onConfirm}
            className="flex-1 rounded-lg px-4 py-2 text-sm font-semibold" style={{ backgroundColor: BLOCKER_YELLOW, color: "#0A1F44" }}>
            Flag blocker
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function BlockerToggle({ flagged, note, onSave, testPrefix }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const save = async (flag, txt) => {
    await onSave(flag, txt);
    window.dispatchEvent(new Event("blockers-changed"));
  };

  const click = (e) => {
    e.stopPropagation();
    if (flagged) { save(false, ""); return; }
    setText(note || "");
    setOpen(true);
  };

  return (
    <>
      <button data-testid={`${testPrefix}-toggle`} onClick={click} aria-pressed={!!flagged}
        title={flagged ? "Remove the blocker marker" : "Flag as holding up the build (shows a warning in the Review portal)"}
        className="inline-flex items-center text-[10px] font-extrabold px-1.5 py-0.5 rounded-full flex-shrink-0 border transition-colors"
        style={flagged ? { backgroundColor: BLOCKER_YELLOW, color: "#0A1F44", borderColor: "#d4be00" } : { color: "#9CA3AF", borderColor: "#E5E7EB", backgroundColor: "#fff" }}>
        <AlertTriangle size={12} />
      </button>
      {open && (
        <BlockerNoteModal text={text} setText={setText} testPrefix={testPrefix}
          onConfirm={(e) => { e.stopPropagation(); setOpen(false); save(true, text.trim()); }}
          onCancel={(e) => { e.stopPropagation(); setOpen(false); }} />
      )}
    </>
  );
}
